import { Box, Button, FormControl, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { ContentCopy } from "@mui/icons-material";
import { InputBase, InputLabel } from "components/common/Input";
import theme from "lib/theme";
import { useFormik } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useCreateOauthApplication from "hooks/api/useCreateOauthApplication";
import { OauthApplication } from "types/oauthApplication";

function CopyableField({ label, value }: { label: string; value: string }) {
  return (
    <TextField
      fullWidth
      label={label}
      size="small"
      variant="standard"
      value={value}
      InputProps={{
        readOnly: true,
        sx: {
          fontFamily: "monospace",
          fontSize: "14px",
        },
        endAdornment: (
          <Tooltip placement="top" title="Copy">
            <IconButton onClick={() => navigator.clipboard.writeText(value)}>
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      }}
    />
  );
}

function OauthApplicationForm() {
  const navigate = useNavigate();
  const { create } = useCreateOauthApplication();
  const [createdApplication, setCreatedApplication] = useState<OauthApplication>();
  const formik = useFormik({
    initialValues: { name: "" },
    enableReinitialize: true,
    onSubmit: (values) => create(values).then(setCreatedApplication),
  });

  return (
    <div>
      <Typography variant="h4" fontWeight="bold">
        Create Oauth Application
      </Typography>

      <Box
        sx={{
          mt: 3,
          px: 5,
          py: 3,
          background: "white",
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {createdApplication ? (
          <>
            <Typography fontWeight="bold">Copy your client secret now</Typography>
            <Typography mt={1}>
              You will not be able to see this secret again. If you lose it, delete this application and create a new
              one.
            </Typography>

            <Box sx={{ mt: 4, display: "grid", gridTemplateColumns: `1fr 1fr`, gap: 3 }}>
              <CopyableField label="Client ID" value={createdApplication.uid} />
              <CopyableField label="Client Secret" value={createdApplication.secret ?? ""} />
            </Box>

            <Box textAlign="right" mt={3}>
              <Button variant="contained" size="large" onClick={() => navigate("/api")}>
                Done
              </Button>
            </Box>
          </>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ mt: 4, display: "grid", gridTemplateColumns: `1fr 1fr`, gap: 3 }}>
              <FormControl variant="standard">
                <InputLabel shrink htmlFor="company-name">
                  Name
                </InputLabel>
                <InputBase
                  type="text"
                  id="oauth-application"
                  placeholder="Name of your application.."
                  {...formik.getFieldProps("name")}
                />
              </FormControl>
            </Box>

            <Box textAlign="right" mt={3}>
              <Button type="submit" variant="contained" size="large">
                Create
              </Button>
            </Box>
          </form>
        )}
      </Box>
    </div>
  );
}

export default OauthApplicationForm;
